import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Copy, Check, ChevronDown, ChevronUp,
  Layers, Server, Database, Cloud, Shield, Zap, Globe, GitBranch,
  BarChart3, Activity, Users, Target, Lightbulb, DollarSign, Sparkles,
  History, CreditCard, Brain, Megaphone, TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  Frontend: Layers,
  Backend: Server,
  Database: Database,
  Hosting: Cloud,
  Authentication: Shield,
  Caching: Zap,
  APIs: Globe,
  DevOps: GitBranch,
  Analytics: BarChart3,
  Monitoring: Activity,
  "AI Model": Brain,
  "Payment Integration": CreditCard,
};


const TechStackCard = ({ item, index, onCompare }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = categoryIcons[item.category] || Layers;

  // Force wide layout for AI Model and Payment Integration
  const isWideCard = item.category === "AI Model" || item.category === "Payment Integration";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className={`tech-card glass-card rounded-2xl p-6 h-full flex flex-col ${isWideCard ? 'wide-card' : ''}`}
      data-testid={`tech-card-${item.category.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gold-gradient flex items-center justify-center">
            <Icon className="w-5 h-5 text-black" />
          </div>
          <div>
            <span className="font-secondary font-bold text-xs tracking-widest uppercase text-gold-400">
              {item.category}
            </span>
            <h3 className="font-primary font-semibold text-lg text-white">
              {item.recommended}
            </h3>
          </div>
        </div>
      </div>

      {/* What it is */}
      <p className="font-body text-sm text-zinc-400 mb-4">
        {item.what_it_is}
      </p>

      {/* Why needed collapsible */}
      <div className="flex-1">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-gold-400 hover:text-gold-300 transition-colors mb-2"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? "Show less" : "Why this choice?"}
        </button>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-3 text-sm"
          >
            <div>
              <span className="font-semibold text-zinc-300">Why needed:</span>
              <p className="text-zinc-400 mt-1">{item.why_needed}</p>
            </div>
            <div>
              <span className="font-semibold text-zinc-300">Best fit because:</span>
              <p className="text-zinc-400 mt-1">{item.why_best_fit}</p>
            </div>
            <div>
              <span className="font-semibold text-zinc-300">Scalability:</span>
              <p className="text-zinc-400 mt-1">{item.scalability}</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Compare button */}
      <button
        onClick={() => onCompare(item)}
        className="mt-4 w-full py-2 rounded-xl border border-white/10 text-sm text-zinc-300 hover:border-gold-500/50 hover:text-gold-400 transition-all"
        data-testid={`compare-btn-${item.category.toLowerCase().replace(/\s+/g, '-')}`}
      >
        Compare Alternatives
      </button>
    </motion.div>
  );
};

const ComparisonModal = ({ isOpen, onClose, item, comparison }) => {
  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-obsidian-200 border border-white/10 max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="font-primary text-xl text-white flex items-center gap-2">
            <span className="gold-text">{item.category}</span> Alternatives
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {comparison ? (
            <div className="space-y-6">
              {/* Recommended */}
              <div className="p-4 rounded-xl border-2 border-gold-500/50 bg-gold-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-gold-400" />
                  <span className="font-semibold text-white">{comparison.recommended}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-gold-500/20 text-gold-400">Recommended</span>
                </div>
                <p className="text-sm text-zinc-400">{comparison.why_recommended_wins}</p>
              </div>

              {/* Alternatives */}
              <div className="space-y-4">
                {comparison.alternatives?.map((alt, i) => (
                  <div key={i} className="p-4 rounded-xl border border-white/10 bg-white/5">
                    <h4 className="font-semibold text-white mb-3">{alt.name}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Pros</span>
                        <ul className="mt-2 space-y-1">
                          {alt.pros?.map((pro, j) => (
                            <li key={j} className="text-sm text-zinc-400 flex items-start gap-2">
                              <span className="text-emerald-400 mt-1">+</span>
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Cons</span>
                        <ul className="mt-2 space-y-1">
                          {alt.cons?.map((con, j) => (
                            <li key={j} className="text-sm text-zinc-400 flex items-start gap-2">
                              <span className="text-red-400 mt-1">−</span>
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const ClassificationCard = ({ classification }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-card rounded-2xl p-6 mb-8"
    data-testid="classification-card"
  >
    <h2 className="font-primary font-semibold text-xl text-white mb-4 flex items-center gap-2">
      <Target className="w-5 h-5 text-gold-400" />
      Idea Classification
    </h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[
        { label: "App Type", value: classification.app_type },
        { label: "Target Users", value: classification.target_users },
        { label: "Expected Scale", value: classification.expected_scale },
        { label: "Complexity", value: classification.complexity_level },
      ].map((item, i) => (
        <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/5">
          <span className="text-xs text-zinc-500 uppercase tracking-wider">{item.label}</span>
          <p className="text-white font-medium mt-1">{item.value}</p>
        </div>
      ))}
    </div>
    {(classification.needs_ai || classification.needs_payments) && (
      <div className="flex gap-3 mt-4">
        {classification.needs_ai && (
          <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs font-semibold">
            AI Powered
          </span>
        )}
        {classification.needs_payments && (
          <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold">
            Payments Required
          </span>
        )}
      </div>
    )}
  </motion.div>
);

const MarketAnalysisSection = ({ marketAnalysis }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5 }}
    className="glass-card rounded-2xl p-6 mb-8"
    data-testid="market-analysis"
  >
    <h2 className="font-primary font-semibold text-xl text-white mb-6 flex items-center gap-2">
      <Users className="w-5 h-5 text-gold-400" />
      Market and Competitor Analysis
    </h2>

    {/* Competitors */}
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-zinc-300 mb-4 uppercase tracking-wider">Competitors</h3>
      <div className="space-y-4">
        {marketAnalysis.competitors?.map((competitor, i) => (
          <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/5">
            <h4 className="font-semibold text-white mb-3">{competitor.name}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-emerald-400 uppercase tracking-wider">Strengths</span>
                <ul className="mt-2 space-y-1">
                  {competitor.strengths?.map((s, j) => (
                    <li key={j} className="text-sm text-zinc-400">• {s}</li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="text-xs text-red-400 uppercase tracking-wider">Weaknesses</span>
                <ul className="mt-2 space-y-1">
                  {competitor.weaknesses?.map((w, j) => (
                    <li key={j} className="text-sm text-zinc-400">• {w}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Market Gaps */}
    <div>
      <h3 className="text-sm font-semibold text-zinc-300 mb-4 uppercase tracking-wider">Market Gaps</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {marketAnalysis.market_gaps?.map((gap, i) => (
          <div key={i} className="p-3 rounded-lg bg-gold-500/10 border border-gold-500/20 text-sm text-zinc-300">
            <Sparkles className="w-4 h-4 text-gold-400 inline mr-2" />
            {gap}
          </div>
        ))}
      </div>
    </div>
  </motion.div>
);

const MarketingSection = ({ marketing }) => {
  if (!marketing) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.55 }}
      className="glass-card rounded-2xl p-6 mb-8"
      data-testid="marketing-section"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Where to Market */}
        <div>
          <h2 className="font-primary font-semibold text-xl text-white mb-6 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-gold-400" />
            Where to Market
          </h2>
          <div className="space-y-3">
            {marketing.where_to_market?.map((platform, i) => (
              <div
                key={i}
                className="p-4 rounded-xl bg-white/5 border border-white/5 flex items-start gap-4"
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gold-gradient flex items-center justify-center text-black font-bold text-sm">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-white">{platform.name}</h4>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                      {platform.platform_type}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-400">{platform.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How to Market */}
        <div>
          <h2 className="font-primary font-semibold text-xl text-white mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-gold-400" />
            How to Market
          </h2>
          <div className="space-y-3">
            {marketing.how_to_market?.map((item, i) => (
              <div
                key={i}
                className="p-4 rounded-xl bg-white/5 border border-white/5 flex items-start gap-4"
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gold-gradient flex items-center justify-center text-black font-bold text-sm">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <h4 className="font-semibold text-white mb-1">{item.strategy}</h4>
                  <p className="text-sm text-zinc-400">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const DifferentiationSection = ({ differentiation }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.6 }}
    className="glass-card rounded-2xl p-6 mb-8"
    data-testid="differentiation"
  >
    <h2 className="font-primary font-semibold text-xl text-white mb-6 flex items-center gap-2">
      <Lightbulb className="w-5 h-5 text-gold-400" />
      Differentiation Ideas
    </h2>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Features */}
      <div>
        <h3 className="text-sm font-semibold text-emerald-400 mb-3 uppercase tracking-wider flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Unique Features
        </h3>
        <ul className="space-y-2">
          {differentiation.features?.map((f, i) => (
            <li key={i} className="text-sm text-zinc-400 p-2 rounded-lg bg-white/5">
              {f}
            </li>
          ))}
        </ul>
      </div>

      {/* UX Improvements */}
      <div>
        <h3 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wider flex items-center gap-2">
          <Layers className="w-4 h-4" />
          UX Improvements
        </h3>
        <ul className="space-y-2">
          {differentiation.ux_improvements?.map((u, i) => (
            <li key={i} className="text-sm text-zinc-400 p-2 rounded-lg bg-white/5">
              {u}
            </li>
          ))}
        </ul>
      </div>

      {/* Monetization */}
      <div>
        <h3 className="text-sm font-semibold text-gold-400 mb-3 uppercase tracking-wider flex items-center gap-2">
          <DollarSign className="w-4 h-4" />
          Monetization
        </h3>
        <ul className="space-y-2">
          {differentiation.monetization?.map((m, i) => (
            <li key={i} className="text-sm text-zinc-400 p-2 rounded-lg bg-white/5">
              {m}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </motion.div>
);

const BuildPromptSection = ({ buildPrompt }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildPrompt);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
      className="glass-card rounded-2xl p-6"
      data-testid="build-prompt"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-primary font-semibold text-xl text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold-400" />
          AI Build Prompt
        </h2>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${copied
            ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50"
            : "bg-gold-gradient text-black hover:shadow-gold"
            }`}
          data-testid="copy-prompt-btn"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy Prompt
            </>
          )}
        </button>
      </div>

      <p className="text-sm text-zinc-400 mb-4">
        Copy this prompt and paste it into any AI coding tool (Claude, GPT, Cursor, etc.) to start building.
      </p>

      <ScrollArea className="h-[400px] rounded-xl border border-white/10 bg-black/50 p-4">
        <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-mono">
          {buildPrompt}
        </pre>
      </ScrollArea>
    </motion.div>
  );
};

export default function ResultsPage() {
  const [result, setResult] = useState(null);
  const [compareItem, setCompareItem] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const navigate = useNavigate();
  const { getToken } = useAuth();

  useEffect(() => {
    const stored = sessionStorage.getItem("analysisResult");
    if (stored) {
      setResult(JSON.parse(stored));
    } else {
      navigate("/");
    }
  }, [navigate]);

  const handleCompare = async (item) => {
    setCompareItem(item);
    setComparison(null);
    setLoadingComparison(true);

    try {
      const token = await getToken();
      const response = await fetch(`${API}/compare`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: item.category,
          recommended: item.recommended,
          context: `${item.what_it_is}. ${item.why_best_fit}`,
        }),
      });

      if (!response.ok) throw new Error('Comparison failed');

      const data = await response.json();
      setComparison(data);
    } catch (error) {
      console.error("Comparison error:", error);
      toast.error("Failed to load comparison");
    } finally {
      setLoadingComparison(false);
    }
  };

  if (!result) {
    return (
      <div className="min-h-screen bg-obsidian-300 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian-300 relative">
      {/* Main Content */}
      <main className="max-w-[1200px] mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-zinc-400 hover:text-gold-400 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              New Analysis
            </button>
            <Link
              to="/history"
              className="flex items-center gap-2 text-zinc-400 hover:text-gold-400 transition-colors"
              data-testid="history-link"
            >
              <History className="w-5 h-5" />
              History
            </Link>
          </div>
          <span className="font-secondary font-bold text-sm tracking-[0.3em] uppercase text-gold-400">
            StackVibecode
          </span>
        </div>

        {/* Idea Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <span className="text-sm text-zinc-500 uppercase tracking-wider">Analysis for</span>
          <h1 className="font-primary font-bold text-2xl md:text-3xl text-white mt-2">
            {result.idea}
          </h1>
        </motion.div>

        {/* Classification */}
        <ClassificationCard classification={result.classification} />

        {/* Tech Stack Grid */}
        <div className="mb-8">
          <h2 className="font-primary font-semibold text-xl text-white mb-6 flex items-center gap-2">
            <Layers className="w-5 h-5 text-gold-400" />
            Recommended Tech Stack
          </h2>
          <div className="bento-grid">
            {result.tech_stack?.map((item, index) => (
              <TechStackCard
                key={item.category}
                item={item}
                index={index}
                onCompare={handleCompare}
              />
            ))}
          </div>
        </div>

        {/* Market Analysis */}
        <MarketAnalysisSection marketAnalysis={result.market_analysis} />

        {/* Marketing Strategies */}
        <MarketingSection marketing={result.marketing} />

        {/* Differentiation */}
        <DifferentiationSection differentiation={result.differentiation} />

        {/* Build Prompt */}
        <BuildPromptSection buildPrompt={result.build_prompt} />
      </main>

      {/* StackVibecode Footer Banner */}
      <div className="w-full bg-gold-500 py-24 md:py-32 lg:py-40">
        <p className="text-center font-secondary font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl tracking-[0.15em] uppercase text-black">
          StackVibecode
        </p>
      </div>

      {/* Comparison Modal */}
      <ComparisonModal
        isOpen={!!compareItem}
        onClose={() => setCompareItem(null)}
        item={compareItem}
        comparison={comparison}
      />
    </div>
  );
}
